import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { getProducts, getAllProducts, getAllProductsBySales, getCustomers, createInvoice, downloadInvoicePDF, getBranchInventory, getBranches, getTailoringOrder, downloadTailoringReceipt, sendTailoringReceipt, updateCustomer, checkCashRegister, getServiceOrder, updateServiceOrderStatus, downloadServiceReceipt, sendServiceReceipt, getCompanyConfig, getPrintConfig, formatPhone, updateTailoringOrderStatus } from '../lib/api';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { ShoppingCart, Trash2, Plus, Minus, Receipt, Search, X, Building2 } from 'lucide-react';


const PosPager = ({ posPage, posTotalPages, setPosPage, filteredProducts }) => (
  posTotalPages <= 1 ? null : (
    <div className="flex-shrink-0 flex items-center justify-between px-4 py-2 border-t border-slate-200 bg-white">
      <span className="text-xs text-slate-500">{filteredProducts.length} prods · Pág {posPage}/{posTotalPages}</span>
      <div className="flex items-center gap-1">
        <button onClick={() => setPosPage(1)} disabled={posPage === 1}
          className="px-2 py-1 rounded text-xs border border-slate-200 disabled:opacity-40 hover:bg-slate-50 bg-white">«</button>
        <button onClick={() => setPosPage(p => Math.max(1, p - 1))} disabled={posPage === 1}
          className="px-2 py-1 rounded text-xs border border-slate-200 disabled:opacity-40 hover:bg-slate-50 bg-white">‹</button>
        {Array.from({length: posTotalPages}, (_, i) => i + 1).filter(p => Math.abs(p - posPage) <= 2).map(p => (
          <button key={p} onClick={() => setPosPage(p)}
            className={`px-2 py-1 rounded text-xs border ${p === posPage ? 'bg-cyan-500 text-white border-cyan-500' : 'border-slate-200 hover:bg-slate-50 bg-white'}`}>
            {p}
          </button>
        ))}
        <button onClick={() => setPosPage(p => Math.min(posTotalPages, p + 1))} disabled={posPage >= posTotalPages}
          className="px-2 py-1 rounded text-xs border border-slate-200 disabled:opacity-40 hover:bg-slate-50 bg-white">›</button>
        <button onClick={() => setPosPage(posTotalPages)} disabled={posPage >= posTotalPages}
          className="px-2 py-1 rounded text-xs border border-slate-200 disabled:opacity-40 hover:bg-slate-50 bg-white">»</button>
      </div>
    </div>
  )
);

// Compact variant chips with expand/collapse
const VariantChips = ({ variants, cajaOpen, onAdd, productId }) => {
  const [expanded, setExpanded] = React.useState(false);
  const LIMIT = 4;
  const visible = expanded ? variants : variants.slice(0, LIMIT);
  const hidden  = variants.length - LIMIT;

  return (
    <div className="flex flex-wrap gap-1.5">
      {visible.map((v, idx) => {
        const label = [v.size, v.color].filter(Boolean).join(' · ') || 'Único';
        const outOfStock = v.stock <= 0;
        return (
          <button
            key={idx}
            onClick={() => !outOfStock && onAdd(v)}
            disabled={outOfStock}
            data-testid={`add-to-cart-${productId}-${idx}`}
            title={outOfStock ? 'Sin stock' : `Agregar: ${label} (${v.stock} disponibles)${!cajaOpen ? ' — Caja cerrada' : ''}`}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border transition-colors
              ${outOfStock
                ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed line-through'
                : cajaOpen
                  ? 'bg-slate-900 text-white border-slate-900 hover:bg-slate-700 cursor-pointer'
                  : 'bg-slate-300 text-slate-600 border-slate-300 cursor-not-allowed'
              }`}
          >
            {label}
            {!outOfStock && <span className="opacity-60">({v.stock})</span>}
            {!cajaOpen && !outOfStock && ' 🔒'}
          </button>
        );
      })}
      {!expanded && hidden > 0 && (
        <button onClick={() => setExpanded(true)}
          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border border-cyan-300 text-cyan-600 bg-cyan-50 hover:bg-cyan-100 transition-colors">
          +{hidden} más
        </button>
      )}
      {expanded && variants.length > LIMIT && (
        <button onClick={() => setExpanded(false)}
          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border border-slate-300 text-slate-500 hover:bg-slate-50 transition-colors">
          Ver menos
        </button>
      )}
    </div>
  );
};

const POS = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const _urlParams        = new URLSearchParams(location.search);
  const tailoringOrderId  = _urlParams.get('tailoring_order_id') || '';
  const serviceOrderId    = _urlParams.get('service_order_id') || '';
  const urlInitialPayment = parseFloat(_urlParams.get('initial_payment') || '0') || 0;
  const urlPaymentMethod  = _urlParams.get('payment_method') || '';
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(user?.branch_id || '');
  const [branchInventory, setBranchInventory] = useState([]); // Inventario por sucursal
  const [cart, setCart] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [invoiceType, setInvoiceType] = useState('consumo');
  const [paymentMethod, setPaymentMethod] = useState('efectivo');
  const [includeItbis, setIncludeItbis] = useState(false);
  const [shipping, setShipping] = useState(0);
  const [enableShipping, setEnableShipping] = useState(false);
  const [deliveryPromptEnabled, setDeliveryPromptEnabled] = useState(true);
  const discount = 0;
  const [cardSurchargePct, setCardSurchargePct] = useState(0);
  const [showPriceNote, setShowPriceNote] = useState(() => sessionStorage.getItem('priceNoteDismissed') !== '1');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [cajaOpen, setCajaOpen] = useState(true);
  const [cajaStale, setCajaStale] = useState(false);
  const [cajaStaleDate, setCajaStaleDate] = useState('');
  const [posPage, setPosPage] = useState(1);
  const posPerPage = 12;
  const pendingNavigateRef = React.useRef(null);
  const pendingDeliveryRef = React.useRef(null); // { orderId, orderCode } – set when fully paid
  const [deliveryPrompt, setDeliveryPrompt] = useState(null);
  const [receiptModal, setReceiptModal] = useState(null);
  const [receiptTimer, setReceiptTimer] = useState(0);
  const [receiptTimerActive, setReceiptTimerActive] = useState(false);
  const [sendingReceipt, setSendingReceipt] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [askUpdateEmail, setAskUpdateEmail] = useState(null);
  const [showCart, setShowCart] = useState(false); // Para mobile
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [cashInputAmount, setCashInputAmount] = useState('');
  const [secondInputAmount, setSecondInputAmount] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [customerSearching, setCustomerSearching] = useState(false);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [tailoringOrder, setTailoringOrder] = useState(null);
  const [prefilledTailoringOrderId, setPrefilledTailoringOrderId] = useState('');
  const [tailoringPaymentAmount, setTailoringPaymentAmount] = useState('');
  const [serviceOrder, setServiceOrder] = useState(null);
  const [prefilledServiceOrderId, setPrefilledServiceOrderId] = useState('');
  const [servicePaymentAmount, setServicePaymentAmount] = useState('');

  useEffect(() => {
    checkCashRegister().then(res => {
      setCajaOpen(!res.required || res.open);
      setCajaStale(res.stale || false);
      setCajaStaleDate(res.stale_date || '');
    }).catch(() => setCajaOpen(true));
  }, []);

  useEffect(() => {
    loadInitialData();
    getCompanyConfig().then(cfg => setCardSurchargePct(parseFloat(cfg?.card_surcharge_pct) || 0)).catch(() => {});
    getPrintConfig().then(cfg => {
      setEnableShipping(cfg?.enable_shipping ?? false);
      setDeliveryPromptEnabled(cfg?.delivery_prompt_on_full_payment ?? true);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedBranch) {
      loadBranchInventory(selectedBranch);
    }
  }, [selectedBranch]);

  useEffect(() => {
    if (!receiptTimerActive || receiptTimer <= 0) return;
    const interval = setInterval(() => {
      setReceiptTimer((t) => {
        if (t <= 1) { clearInterval(interval); setReceiptTimerActive(false); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [receiptTimerActive]);

  useEffect(() => {
    if (receiptTimer !== 0 || !receiptModal?.customerEmail || showEmailInput || sendingReceipt) return;
    doSendEmail(receiptModal.customerEmail, receiptModal.tailoringOrderId, receiptModal.serviceOrderId, receiptModal.invoice?.id);
  }, [receiptTimer]); // eslint-disable-line

  useEffect(() => {
    if (customerSearch.length < 2) { setCustomerResults([]); setShowCustomerDropdown(false); return; }
    const timer = setTimeout(async () => {
      setCustomerSearching(true);
      try {
        const data = await getCustomers({ q: customerSearch, per_page: 8 });
        setCustomerResults(data.customers || data || []);
        setShowCustomerDropdown(true);
      } catch { setCustomerResults([]); }
      finally { setCustomerSearching(false); }
    }, 400);
    return () => clearTimeout(timer);
  }, [customerSearch]);

  useEffect(() => {
    if (tailoringOrderId && products.length > 0 && customers.length > 0 && prefilledTailoringOrderId !== tailoringOrderId) {
      loadTailoringOrderForPOS(tailoringOrderId);
    }

  }, [tailoringOrderId, products.length, customers.length, prefilledTailoringOrderId]);

  useEffect(() => {
    if (serviceOrderId && customers.length > 0 && prefilledServiceOrderId !== serviceOrderId) {
      loadServiceOrderForPOS(serviceOrderId);
    }
  }, [serviceOrderId, customers.length, prefilledServiceOrderId]); // eslint-disable-line

  const loadInitialData = async () => {
    try {
      const branchId = user?.branch_id || '';
      const [productsData, customersData, branchesData] = await Promise.all([
        getAllProductsBySales(branchId),
        getCustomers(),
        user?.role === 'admin' ? getBranches() : Promise.resolve([])
      ]);
      setProducts(productsData);
      setCustomers(customersData.customers || customersData);
      setBranches(branchesData);

      // Si el usuario tiene sucursal asignada, cargar su inventario
      const resolvedBranchId = branchId || (branchesData.length > 0 ? branchesData[0].id : '');
      if (resolvedBranchId) {
        setSelectedBranch(resolvedBranchId);
      }
    } catch (error) {
      toast.error('Error al cargar datos');
    }
  };

  const loadBranchInventory = async (branchId) => {
    try {
      const inventoryData = await getBranchInventory(branchId);
      setBranchInventory(inventoryData);
      // Limpiar carrito al cambiar de sucursal, excepto cuando viene cargado desde pedido
      if (!tailoringOrderId && !serviceOrderId) {
        setCart([]);
      }
    } catch (error) {
      toast.error('Error al cargar inventario');
    }
  };


  const toNumber = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };

  const buildTailoringCartItems = (order, amountToInvoice = null) => {
    const totalAmount   = toNumber(order?.pricing?.total_amount);
    const paidAmount    = toNumber(order?.pricing?.paid_amount);
    const balance       = Math.max(0, totalAmount - paidAmount);
    const invoiceAmount = amountToInvoice === null ? balance : toNumber(amountToInvoice);

    return [{
      product_id:      `tailoring-order-${order?.id}`,
      product_name:    `Pedido ${order?.code || order?.id || ''}`,
      product_type:    'SERVICIO',
      track_inventory: false,
      quantity:        1,
      unit_price:      invoiceAmount,
      size:            '',
      color:           '',
      discount:        0,
      subtotal:        invoiceAmount,
      locked:          true,
    }];
  };

  const downloadAndPrint = async (tailoringOrderId, serviceOrderId, invoiceId) => {
    try {
      let blob;
      if (tailoringOrderId) blob = await downloadTailoringReceipt(tailoringOrderId);
      else if (serviceOrderId) blob = await downloadServiceReceipt(serviceOrderId);
      else blob = await downloadInvoicePDF(invoiceId);
      if (!blob || blob.type === 'application/json') return;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.target = '_blank'; link.rel = 'noopener noreferrer';
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(url), 3000);
    } catch (e) { console.error('Print error:', e); }
  };

  const closeReceiptModal = () => {
    setReceiptModal(null); setReceiptTimerActive(false); setReceiptTimer(0);
    setEmailInput(''); setShowEmailInput(false); setAskUpdateEmail(null);
    if (pendingDeliveryRef.current) {
      const delivery = pendingDeliveryRef.current;
      pendingDeliveryRef.current = null;
      setDeliveryPrompt(delivery);
      return;
    }
    if (pendingNavigateRef.current) {
      const path = pendingNavigateRef.current;
      pendingNavigateRef.current = null;
      navigate(path);
    }
  };

  const doSendEmail = async (email, tailoringOrderId, serviceOrderId, invoiceId) => {
    setSendingReceipt(true);
    try {
      if (tailoringOrderId) await sendTailoringReceipt(tailoringOrderId, email);
      else if (serviceOrderId) await sendServiceReceipt(serviceOrderId, email);
      toast.success(`Comprobante enviado a ${email}`);
    } catch (e) { toast.error('Error enviando comprobante'); }
    finally { setSendingReceipt(false); closeReceiptModal(); }
  };

  const handleReceiptModalEmail = async () => {
    const email = emailInput.trim();
    if (!email || !email.includes('@')) { toast.error('Correo inválido'); return; }
    setReceiptTimerActive(false);
    if (receiptModal?.customerEmail && email !== receiptModal.customerEmail && receiptModal?.customerId) {
      setAskUpdateEmail(email); return;
    }
    await doSendEmail(email, receiptModal?.tailoringOrderId, receiptModal?.serviceOrderId, receiptModal?.invoice?.id);
  };

  const confirmUpdateEmail = async (doUpdate) => {
    const email = askUpdateEmail;
    if (doUpdate && receiptModal?.customerId) {
      try { await updateCustomer(receiptModal.customerId, { email }); toast.success('Correo actualizado'); }
      catch (e) { console.error(e); }
    }
    setAskUpdateEmail(null);
    await doSendEmail(email, receiptModal?.tailoringOrderId, receiptModal?.serviceOrderId, receiptModal?.invoice?.id);
  };

  const openReceiptModal = (invoice, tailoringOrderId, serviceOrderId, customer) => {
    const pref  = user?.receipt_preference || 'ask';
    const timer = user?.receipt_timer || 15;
    const hasEmail = !!customer?.email;
    if (pref === 'print') { downloadAndPrint(tailoringOrderId, serviceOrderId, invoice?.id); return; }
    if (pref === 'email' && hasEmail) {
      setReceiptModal({ invoice, tailoringOrderId, serviceOrderId, customerEmail: customer.email, customerName: `${customer.name} ${customer.last_name || ''}`.trim(), customerId: customer.id });
      setReceiptTimer(timer); setReceiptTimerActive(true); return;
    }
    if (pref === 'email' && !hasEmail) {
      setReceiptModal({ invoice, tailoringOrderId, serviceOrderId, customerEmail: '', customerName: customer ? `${customer.name} ${customer.last_name || ''}`.trim() : '', customerId: customer?.id });
      setShowEmailInput(true); return;
    }
    setReceiptModal({ invoice, tailoringOrderId, serviceOrderId, customerEmail: customer?.email || '', customerName: customer ? `${customer.name} ${customer.last_name || ''}`.trim() : 'Cliente', customerId: customer?.id });
    if (hasEmail) { setReceiptTimer(timer); setReceiptTimerActive(true); }
  };

  const loadTailoringOrderForPOS = async (orderId) => {
    try {
      const order = await getTailoringOrder(orderId);
      const totalAmount = toNumber(order?.pricing?.total_amount);
      const paidAmount = toNumber(order?.pricing?.paid_amount);
      const balance = Math.max(0, totalAmount - paidAmount);

      if (order?.status === 'CANCELADO') {
        toast.error('No se puede facturar un pedido cancelado');
        return;
      }
      if (balance <= 0) {
        toast.error('Este pedido ya está saldado');
        navigate(`/tailoring-orders/${orderId}`);
        return;
      }

      setTailoringOrder(order);
      setPrefilledTailoringOrderId(orderId);
      if (order.branch_id) {
        setSelectedBranch(order.branch_id);
      }

      const customer = customers.find((c) => c.id === order.customer_id);
      if (customer) {
        setSelectedCustomer(customer);
      } else {
        setSelectedCustomer({ id: order.customer_id, name: order.customer_name || 'Cliente', last_name: '' });
      }

      setIncludeItbis(false);
      // Use initial_payment from URL if provided (includes services), else use balance
      const amountToCharge = urlInitialPayment > 0 ? urlInitialPayment : balance;
      setTailoringPaymentAmount(String(amountToCharge.toFixed(2)));
      setCart(buildTailoringCartItems(order, amountToCharge, false));

      if (urlPaymentMethod && ['efectivo', 'tarjeta', 'transferencia'].includes(urlPaymentMethod)) {
        setPaymentMethod(urlPaymentMethod);
      }

      setSearchTerm('');
      toast.success(`Pedido ${order.code || order.id} cargado en POS`);
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Error cargando pedido en POS');
    }
  };

  const loadServiceOrderForPOS = async (orderId) => {
    try {
      const order = await getServiceOrder(orderId);

      if (order?.status === 'CANCELADO') {
        toast.error('No se puede cobrar una orden cancelada');
        return;
      }
      if (order?.status === 'ENTREGADO') {
        const entregadoBalance = Math.max(0, toNumber(order?.pricing?.total_amount) - toNumber(order?.pricing?.paid_amount));
        if (entregadoBalance <= 0) {
          toast.error('Esta orden ya fue entregada y está saldada');
          navigate(`/service-orders/${orderId}`);
          return;
        }
        // balance pendiente aunque ya fue entregada — permite cobrar
      }

      const total   = toNumber(order?.pricing?.total_amount);
      const paid    = toNumber(order?.pricing?.paid_amount);
      const balance = Math.max(0, total - paid);

      if (balance <= 0 && urlInitialPayment <= 0) {
        toast.error('Esta orden ya está saldada');
        navigate(`/service-orders/${orderId}`);
        return;
      }

      setServiceOrder(order);
      setPrefilledServiceOrderId(orderId);

      if (order.branch_id) setSelectedBranch(order.branch_id);

      const customer = customers.find(c => c.id === order.customer_id);
      if (customer) {
        setSelectedCustomer(customer);
      } else {
        setSelectedCustomer({ id: order.customer_id, name: order.customer_name || 'Cliente', last_name: '' });
      }

      setIncludeItbis(false);
      const amountToCharge = urlInitialPayment > 0 ? urlInitialPayment : balance;
      setServicePaymentAmount(String(amountToCharge.toFixed(2)));

      // Build a simple service cart: one line item per service
      const cartItems = (order.services || []).map(s => {
        const rawTotal = (order.services || []).reduce((sum, svc) => sum + toNumber(svc.subtotal), 0);
        const scale    = rawTotal > 0 ? amountToCharge / rawTotal : 1;
        const subtotal = Math.max(0, toNumber(s.subtotal) * scale);
        const qty      = toNumber(s.quantity) || 1;
        return {
          product_id:      s.product_id,
          product_name:    `${s.name} (${order.code || orderId})`,
          product_type:    'SERVICIO',
          track_inventory: false,
          quantity:        qty,
          unit_price:      subtotal / qty,
          size:            '',
          color:           '',
          discount:        0,
          subtotal,
          locked:          true,
        };
      });

      if (cartItems.length === 0) {
        // Fallback: single line for the full amount
        cartItems.push({
          product_id:      `service-order-${orderId}`,
          product_name:    `Servicio ${order.code || orderId}`,
          product_type:    'SERVICIO',
          track_inventory: false,
          quantity:        1,
          unit_price:      amountToCharge,
          size:            '',
          color:           '',
          discount:        0,
          subtotal:        amountToCharge,
          locked:          true,
        });
      }

      setCart(cartItems);

      if (urlPaymentMethod && ['efectivo', 'tarjeta', 'transferencia'].includes(urlPaymentMethod)) {
        setPaymentMethod(urlPaymentMethod);
      }

      setSearchTerm('');
      toast.success(`Orden de servicio ${order.code || orderId} cargada en POS`);
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Error cargando orden de servicio en POS');
    }
  };

  // Obtener stock de un producto/variante desde el inventario de la sucursal
  const getVariantStock = (productId, size, color) => {
    const inv = branchInventory.find(
      i => i.product_id === productId && 
           (i.size || '') === (size || '') && 
           (i.color || '') === (color || '')
    );
    return inv?.stock || 0;
  };

  const isService = (product) => {
    const productType = String(
      product?.product_type ||
      product?.productType ||
      product?.type ||
      ''
    ).trim().toUpperCase();

    const trackInventory = product?.track_inventory;

    return (
      productType === 'SERVICIO' ||
      productType === 'SERVICE' ||
      trackInventory === false ||
      trackInventory === 'false' ||
      trackInventory === 0
    );
  };

  // Obtener todas las variantes con stock para un producto
  const getProductVariantsWithStock = (product) => {
    if (isService(product)) {
      return [{ size: '', color: '', stock: null, isService: true }];
    }

    // Obtener variantes del inventario de sucursal para este producto
    const productInventory = branchInventory.filter(i => i.product_id === product.id);
    
    if (productInventory.length > 0) {
      return productInventory.map(inv => ({
        size: inv.size || '',
        color: inv.color || '',
        stock: inv.stock || 0
      }));
    }
    
    // Fallback: Si no hay inventario de sucursal, usar las combinaciones definidas en el producto
    // pero con stock 0 (para indicar que no hay stock en esta sucursal)
    const variants = [];
    const sizes = product.sizes?.length > 0 ? product.sizes : [''];
    const colors = product.colors?.length > 0 ? product.colors : [''];
    
    for (const size of sizes) {
      for (const color of colors) {
        variants.push({ size, color, stock: 0 });
      }
    }
    
    return variants;
  };

  const addToCart = (product, size, color, availableStock) => {
    const service = isService(product);

    if (!service && availableStock <= 0) {
      toast.error('Producto sin stock en esta sucursal');
      return;
    }

    const existingItem = cart.find(
      item => item.product_id === product.id && item.size === size && item.color === color
    );

    if (existingItem) {
      if (!service && existingItem.quantity >= availableStock) {
        toast.error('Stock insuficiente');
        return;
      }
      updateQuantity(existingItem, existingItem.quantity + 1);
    } else {
      const newItem = {
        product_id: product.id,
        product_name: product.name,
        product_code: product.code || '',
        product_type: service ? 'SERVICIO' : (product.product_type || 'PRODUCTO'),
        track_inventory: !service,
        quantity: 1,
        unit_price: product.price,
        size: size,
        color: color,
        discount: 0,
        subtotal: product.price
      };
      setCart([...cart, newItem]);
      toast.success(`${product.name} agregado al carrito`);
    }
  };

  const updateQuantity = (item, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(item);
      return;
    }

    // Validar que no exceda el stock disponible solo en productos físicos
    if (item.track_inventory !== false) {
      const availableStock = getVariantStock(item.product_id, item.size, item.color);
      if (newQuantity > availableStock) {
        toast.error('Stock insuficiente');
        return;
      }
    }

    const updatedCart = cart.map(cartItem => {
      if (cartItem === item) {
        return {
          ...cartItem,
          quantity: newQuantity,
          subtotal: cartItem.unit_price * newQuantity
        };
      }
      return cartItem;
    });
    setCart(updatedCart);
  };

  const removeFromCart = (item) => {
    setCart(cart.filter(cartItem => cartItem !== item));
  };

  const fmt = (n) => Number(n).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const calculateTotals = () => {
    const baseSubtotal = cart.reduce((sum, item) => sum + item.subtotal, 0);
    const applyCardSurcharge = (paymentMethod === 'tarjeta' || paymentMethod.includes('tarjeta')) && cardSurchargePct > 0;
    const cardSurchargeAmt = applyCardSurcharge ? Math.round(baseSubtotal * cardSurchargePct / 100 * 100) / 100 : 0;
    const subtotal = baseSubtotal;
    const itbis = includeItbis ? (subtotal + cardSurchargeAmt) * 0.18 : 0;
    const total = subtotal + cardSurchargeAmt + itbis - discount + shipping;
    return { subtotal, itbis, total, cardSurchargeAmt };
  };

  const handleTailoringPaymentAmountChange = (value) => {
    setTailoringPaymentAmount(value);

    if (!tailoringOrder) return;

    const totalAmount = toNumber(tailoringOrder?.pricing?.total_amount);
    const paidAmount = toNumber(tailoringOrder?.pricing?.paid_amount);
    const balance = Math.max(0, totalAmount - paidAmount);
    let amount = toNumber(value);

    if (amount < 0) amount = 0;
    if (amount > balance) amount = balance;

    setCart(buildTailoringCartItems(tailoringOrder, amount, includeItbis));
  };

  useEffect(() => {
    if (paymentMethod.includes('+')) {
      setIncludeItbis(paymentMethod.includes('tarjeta'));
    } else if (paymentMethod === 'tarjeta') {
      setIncludeItbis(true);
    } else {
      setIncludeItbis(false);
    }
    setCashInputAmount('');
    setSecondInputAmount('');
  }, [paymentMethod]);

  const toggleItbis = () => {
    const next = !includeItbis;
    setIncludeItbis(next);

    if (tailoringOrder) {
      const totalAmount = toNumber(tailoringOrder?.pricing?.total_amount);
      const paidAmount = toNumber(tailoringOrder?.pricing?.paid_amount);
      const balance = Math.max(0, totalAmount - paidAmount);
      let amount = toNumber(tailoringPaymentAmount);
      if (amount <= 0 || amount > balance) amount = balance;
      setCart(buildTailoringCartItems(tailoringOrder, amount, next));
    }
  };

  const handleCheckout = async () => {
    if (cart.length === 0) {
      toast.error('El carrito está vacío');
      return;
    }

    // Validate credit payment requires a customer
    if (paymentMethod === 'credito' && !selectedCustomer) {
      toast.error('Debe seleccionar un cliente para ventas a crédito');
      return;
    }

    // Validar que haya sucursal seleccionada
    if (!selectedBranch) {
      toast.error('Debe seleccionar una sucursal');
      return;
    }

    setLoading(true);
    try {
      let itemsForInvoice = cart;

      if (tailoringOrder) {
        const totalAmount = toNumber(tailoringOrder?.pricing?.total_amount);
        const paidAmount = toNumber(tailoringOrder?.pricing?.paid_amount);
        const balance = Math.max(0, totalAmount - paidAmount);
        const amount = toNumber(tailoringPaymentAmount);

        if (amount <= 0) {
          toast.error('El monto del abono debe ser mayor que cero');
          setLoading(false);
          return;
        }

        if (amount > balance) {
          toast.error('El abono no puede ser mayor al balance pendiente');
          setLoading(false);
          return;
        }

        itemsForInvoice = buildTailoringCartItems(tailoringOrder, amount, includeItbis);
      }

      if (serviceOrder) {
        const amount = toNumber(servicePaymentAmount);
        const total  = toNumber(serviceOrder?.pricing?.total_amount);
        const paid   = toNumber(serviceOrder?.pricing?.paid_amount);
        const balance = Math.max(0, total - paid);
        if (amount <= 0) {
          toast.error('El monto debe ser mayor que cero');
          setLoading(false);
          return;
        }
        if (amount > balance) {
          toast.error('El monto no puede ser mayor al balance pendiente');
          setLoading(false);
          return;
        }
      }

      const invoiceData = {
        customer_id: selectedCustomer?.id || null,
        customer_name: selectedCustomer ? `${selectedCustomer.name} ${selectedCustomer.last_name}` : 'Consumidor Final',
        invoice_type: invoiceType,
        items: itemsForInvoice,
        discount: discount,
        shipping: shipping,
        card_surcharge_pct: cardSurchargePct,
        payment_method: paymentMethod,
        payment_methods: isCombinedPayment ? [
          { method: secondPaymentMethod, amount: secondPaid },
          { method: 'efectivo', amount: Math.max(0, total - secondPaid) }
        ].filter(p => p.amount > 0) : null,
        cash_received: cashPaid > 0 && (paymentMethod === 'efectivo' || isCombinedPayment) ? cashPaid : null,
        change_amount: vuelto > 0 ? vuelto : null,
        include_itbis: includeItbis,
        branch_id: selectedBranch,
        tailoring_order_id: tailoringOrder?.id || null,
        service_order_id: serviceOrder?.id || null,
        notes: tailoringOrder
          ? `Factura generada desde pedido de confección ${tailoringOrder.code || tailoringOrder.id}`
          : serviceOrder
          ? `Factura generada desde orden de servicio ${serviceOrder.code || serviceOrder.id}`
          : ''
      };

      const invoice = await createInvoice(invoiceData, user.id);
      toast.success(`Factura ${invoice.invoice_number} creada exitosamente`);

      const currentCustomer       = selectedCustomer;
      const currentTailoringId    = tailoringOrder?.id || null;
      const currentServiceOrderId = serviceOrder?.id   || null;

      // Detect if order is now fully paid (to prompt for delivery)
      if (deliveryPromptEnabled && tailoringOrder && !['ENTREGADO', 'CANCELADO'].includes(tailoringOrder.status)) {
        const tTotal  = toNumber(tailoringOrder?.pricing?.total_amount);
        const tPaid   = toNumber(tailoringOrder?.pricing?.paid_amount);
        const tAmount = toNumber(tailoringPaymentAmount);
        if (tTotal > 0 && tPaid + tAmount >= tTotal - 0.01) {
          pendingDeliveryRef.current = { orderId: tailoringOrder.id, orderCode: tailoringOrder.code || tailoringOrder.id, type: 'tailoring' };
        }
      } else if (deliveryPromptEnabled && serviceOrder && !['ENTREGADO', 'CANCELADO'].includes(serviceOrder.status)) {
        const sTotal  = toNumber(serviceOrder?.pricing?.total_amount);
        const sPaid   = toNumber(serviceOrder?.pricing?.paid_amount);
        const sAmount = toNumber(servicePaymentAmount);
        if (sTotal > 0 && sPaid + sAmount >= sTotal - 0.01) {
          pendingDeliveryRef.current = { orderId: serviceOrder.id, orderCode: serviceOrder.code || serviceOrder.id, type: 'service' };
        }
      }

      setCart([]);
      setSelectedCustomer(null);
      setCustomerSearch('');
      setShowCustomerDropdown(false);

      setTailoringPaymentAmount('');
      setServicePaymentAmount('');
      setPaymentMethod('efectivo');
      setIncludeItbis(false);
      setShipping(0);
      setCashInputAmount('');
      setSecondInputAmount('');
      loadBranchInventory(selectedBranch);

      if (currentTailoringId) {
        pendingNavigateRef.current = `/tailoring-orders`;
      } else if (currentServiceOrderId) {
        pendingNavigateRef.current = `/service-orders`;
      }
      openReceiptModal(invoice, currentTailoringId, currentServiceOrderId, currentCustomer);
    } catch (error) {
      toast.error(error.response?.data?.detail || error.response?.data?.error || 'Error al crear factura');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { setPosPage(1); }, [searchTerm]);
  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.category.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const posTotalPages = Math.max(1, Math.ceil(filteredProducts.length / posPerPage));
  const paginatedProducts = filteredProducts.slice((posPage - 1) * posPerPage, posPage * posPerPage);

  const isCombinedPayment = paymentMethod.includes('+');
  const secondPaymentMethod = isCombinedPayment ? paymentMethod.split('+')[1] : null;
  const { subtotal, itbis, total, cardSurchargeAmt } = calculateTotals();
  const cashPaid = parseFloat(cashInputAmount) || 0;
  const secondPaid = isCombinedPayment ? (parseFloat(secondInputAmount) || 0) : 0;
  const vuelto = paymentMethod === 'efectivo'
    ? Math.max(0, cashPaid - total)
    : isCombinedPayment
    ? Math.max(0, cashPaid + secondPaid - total)
    : 0;

  // Mostrar advertencia si no hay sucursal seleccionada y no es admin
  if (!selectedBranch && user?.role !== 'admin') {
    return (
      <div className="p-6 md:p-8" data-testid="pos-page">
        <h1 className="text-4xl font-bold mb-6" style={{ fontFamily: 'Outfit' }}>Punto de Venta</h1>
        <div className="bg-amber-50 border border-amber-200 p-6 rounded-lg">
          <div className="flex items-center gap-3 text-amber-800">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <p className="font-semibold">No tiene una sucursal asignada</p>
              <p className="text-sm">Contacte al administrador para que le asigne una sucursal y poder realizar ventas.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Obtener nombre de la sucursal seleccionada
  const currentBranchName = branches.find(b => b.id === selectedBranch)?.name || user?.branch_name || 'Sucursal';

  return (
    <div className="h-full flex flex-col overflow-hidden" data-testid="pos-page">
      {!cajaOpen && cajaStale && (
        <div className="bg-rose-50 border-b border-rose-200 px-4 py-2 flex items-center justify-between">
          <p className="text-sm text-rose-800 font-medium">
            ⚠️ Tienes un turno abierto del <strong>{cajaStaleDate}</strong> — debes cerrarlo y abrir uno nuevo para facturar
          </p>
          <button onClick={() => navigate('/cash-register')}
            className="text-xs bg-rose-500 hover:bg-rose-600 text-white px-3 py-1 rounded-lg font-medium transition-colors">
            Ir a Caja
          </button>
        </div>
      )}
      {!cajaOpen && !cajaStale && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-between">
          <p className="text-sm text-amber-800 font-medium">
            🔒 Caja cerrada — puedes consultar productos pero no facturar
          </p>
          <button onClick={() => navigate('/cash-register')}
            className="text-xs bg-amber-500 hover:bg-amber-600 text-white px-3 py-1 rounded-lg font-medium transition-colors">
            Abrir turno
          </button>
        </div>
      )}
      {/* Header con botón de carrito para mobile */}
      <div className="p-4 bg-white border-b border-slate-200 lg:hidden flex items-center justify-between">
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>POS</h1>
        <Button
          onClick={() => setShowCart(true)}
          className="bg-cyan-500 hover:bg-cyan-600 relative"
          data-testid="mobile-cart-button"
        >
          <ShoppingCart className="w-5 h-5" />
          {cart.length > 0 && (
            <span className="absolute -top-2 -right-2 bg-rose-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center font-bold">
              {cart.length}
            </span>
          )}
        </Button>
      </div>

      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* Products Section */}
        <div className="flex-1 flex flex-col overflow-hidden bg-slate-50">
          {/* STICKY HEADER */}
          <div className="flex-shrink-0 p-4 lg:p-6 pb-0">
          <div className="mb-4 lg:mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
              <h1 className="hidden lg:block text-4xl font-bold" style={{ fontFamily: 'Outfit' }}>Punto de Venta</h1>
              
              {/* Selector de sucursal para admin */}
              {user?.role === 'admin' && branches.length > 0 && (
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-slate-500" />
                  <Select value={selectedBranch} onValueChange={setSelectedBranch} disabled={!!tailoringOrder}>
                    <SelectTrigger className="w-48 bg-white" data-testid="pos-branch-select">
                      <SelectValue placeholder="Seleccionar sucursal" />
                    </SelectTrigger>
                    <SelectContent>
                      {branches.map((branch) => (
                        <SelectItem key={branch.id} value={branch.id}>
                          {branch.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            
            {/* Info de sucursal actual */}
            {selectedBranch && (
              <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-2 mb-4 flex items-center gap-2 text-cyan-800 text-sm">
                <Building2 className="w-4 h-4" />
                <span>Vendiendo en: <strong>{currentBranchName}</strong></span>
              </div>
            )}

            {tailoringOrder && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-amber-900 text-sm flex items-center justify-between gap-3">
                <div>
                  <div className="font-semibold">Facturando pedido de confección {tailoringOrder.code || tailoringOrder.id}</div>
                  <div>Cliente: {tailoringOrder.customer_name}</div>
                </div>
                <Button variant="outline" size="sm" onClick={() => navigate(`/tailoring-orders/${tailoringOrder.id}`)}>
                  Volver al pedido
                </Button>
              </div>
            )}
            {serviceOrder && (
              <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-3 mb-4 text-cyan-900 text-sm flex items-center justify-between gap-3">
                <div>
                  <div className="font-semibold">Cobrando orden de servicio {serviceOrder.code || serviceOrder.id}</div>
                  <div>Cliente: {serviceOrder.customer_name}</div>
                </div>
                <Button variant="outline" size="sm" onClick={() => navigate(`/service-orders/${serviceOrder.id}`)}>
                  Volver a la orden
                </Button>
              </div>
            )}
            
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Buscar productos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white border-slate-200 h-11 lg:h-12"
                data-testid="pos-search-input"
              />
            </div>
          </div>

          <PosPager posPage={posPage} posTotalPages={posTotalPages} setPosPage={setPosPage} filteredProducts={filteredProducts} />
          </div>{/* end sticky header */}

          {/* SCROLLABLE PRODUCTS */}
          <div className="flex-1 overflow-y-auto px-4 lg:px-6 pb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4" data-testid="pos-products-grid">
          {paginatedProducts.map(product => {
            const variants = getProductVariantsWithStock(product);
            const service = isService(product);
            const hasAnyStock = service || variants.some(v => v.stock > 0);
            
            return (
              <Card
                key={product.id}
                className={`cursor-pointer hover:shadow-lg transition-shadow duration-200 border border-slate-200 ${!hasAnyStock ? 'opacity-60' : ''}`}
                data-testid={`product-card-${product.id}`}
              >
                {product.image_url && (
                  <div className="w-full h-28 lg:h-32 overflow-hidden rounded-t-lg bg-slate-100">
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-full h-full object-cover cursor-zoom-in"
                      onClick={(e) => { e.stopPropagation(); setEnlargedImage({ url: product.image_url, name: product.name }); }}
                    />
                  </div>
                )}
                <CardHeader className="p-3 lg:p-4">
                  <CardTitle className="text-base lg:text-lg" style={{ fontFamily: 'Outfit' }}>{product.name}</CardTitle>
                  <p className="text-xs lg:text-sm text-slate-500">{product.category}{service ? ' · Servicio' : ''}</p>
                  {product.code && <span className="text-xs text-slate-400 font-mono">{product.code}</span>}
                  {cardSurchargePct > 0 ? (
                    <div className="flex flex-col gap-0.5">
                      <div className="flex items-center gap-1.5">
                        <span className="text-base lg:text-lg font-bold text-cyan-600" style={{ fontFamily: 'JetBrains Mono' }}>RD${product.price.toLocaleString('es-DO')}</span>
                        <span className="text-xs text-slate-400">efectivo</span>
                        <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">−{cardSurchargePct}%</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-semibold text-blue-600" style={{ fontFamily: 'JetBrains Mono' }}>RD${Math.round(product.price * (1 + cardSurchargePct / 100)).toLocaleString('es-DO')}</span>
                        <span className="text-xs text-slate-400">tarjeta</span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-base lg:text-lg font-bold text-cyan-600" style={{ fontFamily: 'JetBrains Mono' }}>
                      RD${product.price.toLocaleString('es-DO')}
                    </p>
                  )}
                </CardHeader>
                <CardContent className="p-3 lg:p-4 pt-0">
                  {variants.length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-2">Sin variantes definidas</p>
                  ) : service ? (
                    <Button
                      onClick={() => cajaOpen ? addToCart(product, '', '', 999) : toast.warning('Abre un turno de caja para facturar')}
                      className={`w-full text-xs h-8 ${cajaOpen ? 'bg-slate-900 hover:bg-slate-800' : 'bg-slate-400 cursor-not-allowed'}`}
                      size="sm"
                    >
                      Agregar servicio{!cajaOpen && ' 🔒'}
                    </Button>
                  ) : (
                    <VariantChips
                      variants={variants}
                      cajaOpen={cajaOpen}
                      onAdd={(v) => cajaOpen ? addToCart(product, v.size, v.color, v.stock) : toast.warning('Abre un turno de caja para facturar')}
                      productId={product.id}
                    />
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
          </div>{/* end scrollable */}
          <PosPager posPage={posPage} posTotalPages={posTotalPages} setPosPage={setPosPage} filteredProducts={filteredProducts} />
        </div>

      {/* Cart Section - Desktop (sidebar) */}
      <div className="hidden lg:flex w-[400px] bg-white border-l border-slate-200 flex-col" data-testid="pos-cart-desktop">

        {/* Header fijo */}
        <div className="p-6 border-b border-slate-200 flex-shrink-0">
          <h2 className="text-2xl font-bold flex items-center" style={{ fontFamily: 'Outfit' }}>
            <ShoppingCart className="w-6 h-6 mr-2 text-cyan-500" />
            Carrito ({cart.length})
          </h2>
        </div>

        {/* Ítems — zona scrollable, solo cuando hay productos */}
        {cart.length > 0 && (
          <div className="flex-1 overflow-y-auto min-h-0">
            <div className="p-6 space-y-3">
              {cart.map((item, index) => (
                <div key={index} className={`p-3 rounded-lg border ${item.locked ? 'bg-slate-100 border-slate-300' : 'bg-slate-50 border-slate-200'}`} data-testid={`cart-item-${index}`}>
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-medium">{item.product_name}</p>
                      {item.product_code && <span className="text-xs text-slate-400 font-mono">{item.product_code}</span>}
                      <p className="text-sm text-slate-500">{item.size} - {item.color}</p>
                      {item.locked && <span className="text-xs text-slate-400">🔒 De la orden</span>}
                    </div>
                    {!item.locked && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeFromCart(item)}
                        className="text-rose-500 hover:text-rose-700 hover:bg-rose-50"
                        data-testid={`remove-item-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Button
                        size="icon"
                        variant="outline"
                        onClick={() => updateQuantity(item, item.quantity - 1)}
                        className="h-8 w-8"
                        disabled={item.locked}
                        data-testid={`decrease-qty-${index}`}
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                      <span className="w-12 text-center font-bold" style={{ fontFamily: 'JetBrains Mono' }}>
                        {item.quantity}
                      </span>
                      <Button
                        size="icon"
                        variant="outline"
                        onClick={() => updateQuantity(item, item.quantity + 1)}
                        className="h-8 w-8"
                        disabled={item.locked}
                        data-testid={`increase-qty-${index}`}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="font-bold" style={{ fontFamily: 'JetBrains Mono' }}>
                      ${fmt((paymentMethod === 'tarjeta' || paymentMethod.includes('tarjeta')) && cardSurchargePct > 0
                        ? Math.round(item.subtotal * (1 + cardSurchargePct / 100) * 100) / 100
                        : item.subtotal)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Controles + totales — fijos al fondo */}
        <div className="flex-shrink-0 border-t border-slate-200 overflow-y-auto">
          {/* Controles: cliente, tipo, método, ITBIS, cajas opcionales */}
          <div className="p-6 space-y-4">
            <div className="space-y-2">
              <Label>Cliente</Label>
              {(tailoringOrderId || serviceOrderId) ? (
                <p className="text-sm font-medium text-slate-700 px-3 py-2 bg-slate-50 border border-slate-200 rounded-md">
                  {selectedCustomer ? `${selectedCustomer.name} ${selectedCustomer.last_name || ''}`.trim() : 'Cliente'}
                </p>
              ) : selectedCustomer ? (
                <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-md">
                  <span className="flex-1 text-sm font-medium text-slate-700">
                    {`${selectedCustomer.name} ${selectedCustomer.last_name || ''}`.trim()}
                    {selectedCustomer.phone && <span className="text-slate-400 font-normal ml-2">{formatPhone(selectedCustomer.phone)}</span>}
                  </span>
                  <button onClick={() => { setSelectedCustomer(null); setCustomerSearch(''); setShowCustomerDropdown(false); }}
                    className="text-slate-400 hover:text-slate-600 shrink-0"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <div className="relative">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      data-testid="pos-customer-search"
                      type="text"
                      placeholder="Buscar cliente..."
                      value={customerSearch}
                      onChange={(e) => setCustomerSearch(e.target.value)}
                      onFocus={() => customerSearch.length >= 2 && setShowCustomerDropdown(true)}
                      onBlur={() => setTimeout(() => setShowCustomerDropdown(false), 200)}
                      className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400"
                    />
                    {customerSearching && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">...</span>}
                  </div>
                  {showCustomerDropdown && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      <button
                        onMouseDown={() => { setSelectedCustomer(null); setCustomerSearch(''); setShowCustomerDropdown(false); }}
                        className="w-full text-left px-3 py-2 text-sm text-slate-500 hover:bg-slate-50 border-b border-slate-100">
                        Consumidor Final
                      </button>
                      {customerResults.map(c => (
                        <button key={c.id} onMouseDown={() => { setSelectedCustomer(c); setCustomerSearch(''); setShowCustomerDropdown(false); }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-cyan-50 transition-colors">
                          <span className="font-medium">{c.name} {c.last_name || ''}</span>
                          {c.phone && <span className="text-slate-400 ml-2">{formatPhone(c.phone)}</span>}
                        </button>
                      ))}
                      {customerResults.length === 0 && !customerSearching && (
                        <p className="px-3 py-2 text-sm text-slate-400">Sin resultados</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select value={invoiceType} onValueChange={setInvoiceType}>
                  <SelectTrigger data-testid="pos-invoice-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="consumo">Consumo</SelectItem>
                    <SelectItem value="credito_fiscal">Crédito Fiscal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Método de Pago</Label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger data-testid="pos-payment-method-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="efectivo">Efectivo</SelectItem>
                    <SelectItem value="tarjeta">Tarjeta</SelectItem>
                    <SelectItem value="transferencia">Transferencia</SelectItem>
                    <SelectItem value="credito" disabled>Crédito</SelectItem>
                    <SelectItem value="efectivo+tarjeta">Efectivo + Tarjeta</SelectItem>
                    <SelectItem value="efectivo+transferencia">Efectivo + Transferencia</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {cardSurchargePct > 0 && showPriceNote && (
              <div className="flex items-center justify-between gap-3 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-500">
                <span>ℹ️ Pago en <strong className="text-slate-700">EFECTIVO</strong> incluye un {cardSurchargePct}% de descuento · Pago con <strong className="text-slate-700">Tarjeta</strong> se le agrega el ITBIS</span>
                <button type="button" onClick={() => { setShowPriceNote(false); sessionStorage.setItem('priceNoteDismissed', '1'); }}
                  className="flex-shrink-0 text-slate-400 hover:text-slate-600 text-sm leading-none">✕</button>
              </div>
            )}

            {(paymentMethod === 'efectivo' || isCombinedPayment) && (
              <div className="space-y-2 border border-violet-200 rounded-lg p-3 bg-violet-50/40">
                {isCombinedPayment && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500 w-24 shrink-0 capitalize">{secondPaymentMethod}</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={secondInputAmount}
                      onChange={(e) => setSecondInputAmount(e.target.value)}
                      className="flex-1 border border-slate-300 rounded-md px-2 py-1.5 text-sm font-mono"
                    />
                  </div>
                )}
                {isCombinedPayment && secondPaid > 0 && (
                  <div className="flex items-center justify-between text-xs px-1">
                    <span className="text-slate-500">→ Efectivo a cobrar:</span>
                    <span className={`font-mono font-medium ${Math.max(0, total - secondPaid) === 0 ? 'text-emerald-600' : 'text-violet-700'}`}>
                      ${fmt(Math.max(0, total - secondPaid))}
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 w-24 shrink-0">Efectivo recibido</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={cashInputAmount}
                    onChange={(e) => setCashInputAmount(e.target.value)}
                    className="flex-1 border border-slate-300 rounded-md px-2 py-1.5 text-sm font-mono"
                  />
                </div>
                {vuelto > 0 && (
                  <div className="flex items-center justify-between text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-lg">
                    <span>Vuelto</span>
                    <span className="font-mono">${fmt(vuelto)}</span>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label>ITBIS</Label>
              <Button
                type="button"
                variant={includeItbis ? "default" : "outline"}
                onClick={toggleItbis}
                disabled={paymentMethod === 'tarjeta' || (isCombinedPayment && secondPaymentMethod === 'tarjeta')}
                className={includeItbis ? "w-full bg-emerald-600 hover:bg-emerald-700 text-white" : "w-full"}
              >
                {includeItbis ? "ITBIS activado" : "ITBIS desactivado"}
              </Button>
            </div>

          </div>

          {/* Totales + botón */}
          <div className="border-t border-slate-100 p-6 pt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span>Subtotal:</span>
              <span className="mono-font font-medium">${fmt(subtotal + cardSurchargeAmt)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>ITBIS {includeItbis ? '(18%)' : '(no aplicado)'}:</span>
              <span className="mono-font font-medium">${fmt(itbis)}</span>
            </div>
            {enableShipping && (
              <div className="flex items-center justify-between text-sm gap-2">
                <span className="shrink-0">Envío:</span>
                <div className="flex items-center gap-1">
                  <span className="text-slate-400 text-xs">RD$</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={shipping || ''}
                    onChange={(e) => setShipping(Math.max(0, parseFloat(e.target.value) || 0))}
                    placeholder="0.00"
                    className="w-24 text-right border border-slate-200 rounded px-2 py-0.5 text-sm mono-font focus:outline-none focus:ring-1 focus:ring-cyan-400"
                  />
                </div>
              </div>
            )}
            <div className="flex justify-between text-xl font-bold pt-2 border-t border-slate-200">
              <span>TOTAL:</span>
              <span className="mono-font text-cyan-600">${fmt(total)}</span>
            </div>
            <Button
              onClick={() => cajaOpen ? handleCheckout() : toast.warning('Abre un turno de caja para facturar')}
              disabled={loading || cart.length === 0}
              className={`w-full text-white h-12 text-lg font-medium shadow-md mt-2 ${cajaOpen ? 'bg-cyan-500 hover:bg-cyan-600 shadow-cyan-500/20' : 'bg-slate-400 cursor-not-allowed'}`}
              data-testid="pos-checkout-button"
            >
              {loading ? 'Procesando...' : (
                <>
                  <Receipt className="w-5 h-5 mr-2" />
                  {tailoringOrder ? 'Facturar / Registrar abono' : serviceOrder ? 'Cobrar orden de servicio' : 'Generar Factura'}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Cart Overlay */}
      {showCart && (
        <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-50" onClick={() => setShowCart(false)}>
          <div className="absolute right-0 top-0 h-full w-full max-w-sm bg-white flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-xl font-bold flex items-center" style={{ fontFamily: 'Outfit' }}>
                <ShoppingCart className="w-5 h-5 mr-2 text-cyan-500" />
                Carrito ({cart.length})
              </h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowCart(false)}
                className="text-slate-500 hover:text-slate-700"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {cart.map((item, index) => (
                <div key={index} className={`p-3 rounded-lg border ${item.locked ? 'bg-slate-100 border-slate-300' : 'bg-slate-50 border-slate-200'}`} data-testid={`mobile-cart-item-${index}`}>
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-medium text-sm">{item.product_name}</p>
                      {item.product_code && <span className="text-xs text-slate-400 font-mono">{item.product_code}</span>}
                      <p className="text-xs text-slate-500">{item.size} - {item.color}</p>
                      {item.locked && <span className="text-xs text-slate-400">🔒 De la orden</span>}
                    </div>
                    {!item.locked && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeFromCart(item)}
                        className="text-rose-500 hover:text-rose-700 hover:bg-rose-50 h-8 w-8"
                        data-testid={`mobile-remove-item-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Button
                        size="icon"
                        variant="outline"
                        onClick={() => updateQuantity(item, item.quantity - 1)}
                        className="h-7 w-7"
                        disabled={item.locked}
                        data-testid={`mobile-decrease-qty-${index}`}
                      >
                        <Minus className="w-3 h-3" />
                      </Button>
                      <span className="w-8 text-center font-bold text-sm" style={{ fontFamily: 'JetBrains Mono' }}>
                        {item.quantity}
                      </span>
                      <Button
                        size="icon"
                        variant="outline"
                        onClick={() => updateQuantity(item, item.quantity + 1)}
                        className="h-7 w-7"
                        disabled={item.locked}
                        data-testid={`mobile-increase-qty-${index}`}
                      >
                        <Plus className="w-3 h-3" />
                      </Button>
                    </div>
                    <p className="font-bold text-sm" style={{ fontFamily: 'JetBrains Mono' }}>
                      ${fmt((paymentMethod === 'tarjeta' || paymentMethod.includes('tarjeta')) && cardSurchargePct > 0
                        ? Math.round(item.subtotal * (1 + cardSurchargePct / 100) * 100) / 100
                        : item.subtotal)}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-4 border-t border-slate-200 space-y-3">
              <div className="space-y-2">
                <Label className="text-sm">Cliente</Label>
                {(tailoringOrderId || serviceOrderId) ? (
                  <p className="text-sm font-medium text-slate-700 px-3 py-2 bg-slate-50 border border-slate-200 rounded-md">
                    {selectedCustomer ? `${selectedCustomer.name} ${selectedCustomer.last_name || ''}`.trim() : 'Cliente'}
                  </p>
                ) : selectedCustomer ? (
                  <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-md">
                    <span className="flex-1 text-sm font-medium text-slate-700 truncate">
                      {`${selectedCustomer.name} ${selectedCustomer.last_name || ''}`.trim()}
                    </span>
                    <button onClick={() => { setSelectedCustomer(null); setCustomerSearch(''); setShowCustomerDropdown(false); }}
                      className="text-slate-400 hover:text-slate-600 shrink-0"><X className="w-4 h-4" /></button>
                  </div>
                ) : (
                  <div className="relative">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        data-testid="mobile-customer-search"
                        type="text"
                        placeholder="Buscar cliente..."
                        value={customerSearch}
                        onChange={(e) => setCustomerSearch(e.target.value)}
                        onFocus={() => customerSearch.length >= 2 && setShowCustomerDropdown(true)}
                        onBlur={() => setTimeout(() => setShowCustomerDropdown(false), 200)}
                        className="w-full pl-9 pr-3 py-1.5 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400 h-9"
                      />
                      {customerSearching && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">...</span>}
                    </div>
                    {showCustomerDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                        <button
                          onMouseDown={() => { setSelectedCustomer(null); setCustomerSearch(''); setShowCustomerDropdown(false); }}
                          className="w-full text-left px-3 py-2 text-sm text-slate-500 hover:bg-slate-50 border-b border-slate-100">
                          Consumidor Final
                        </button>
                        {customerResults.map(c => (
                          <button key={c.id} onMouseDown={() => { setSelectedCustomer(c); setCustomerSearch(''); setShowCustomerDropdown(false); }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-cyan-50 transition-colors">
                            <span className="font-medium">{c.name} {c.last_name || ''}</span>
                            {c.phone && <span className="text-slate-400 ml-2">{formatPhone(c.phone)}</span>}
                          </button>
                        ))}
                        {customerResults.length === 0 && !customerSearching && (
                          <p className="px-3 py-2 text-sm text-slate-400">Sin resultados</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-2">
                  <Label className="text-sm">Tipo</Label>
                  <Select value={invoiceType} onValueChange={setInvoiceType}>
                    <SelectTrigger className="h-9" data-testid="mobile-invoice-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="consumo">Consumo</SelectItem>
                      <SelectItem value="credito_fiscal">Crédito Fiscal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Método de Pago</Label>
                  <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                    <SelectTrigger className="h-9" data-testid="mobile-payment-method-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="efectivo">Efectivo</SelectItem>
                      <SelectItem value="tarjeta">Tarjeta</SelectItem>
                      <SelectItem value="transferencia">Transferencia</SelectItem>
                      <SelectItem value="credito" disabled>Crédito</SelectItem>
                    <SelectItem value="efectivo+tarjeta">Efectivo + Tarjeta</SelectItem>
                    <SelectItem value="efectivo+transferencia">Efectivo + Transferencia</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {cardSurchargePct > 0 && showPriceNote && (
                <div className="flex items-center justify-between gap-3 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-500">
                  <span>ℹ️ Pago en <strong className="text-slate-700">EFECTIVO</strong> incluye un {cardSurchargePct}% de descuento · Pago con <strong className="text-slate-700">Tarjeta</strong> se le agrega el ITBIS</span>
                  <button type="button" onClick={() => { setShowPriceNote(false); sessionStorage.setItem('priceNoteDismissed', '1'); }}
                    className="flex-shrink-0 text-slate-400 hover:text-slate-600 text-sm leading-none">✕</button>
                </div>
              )}

              {(paymentMethod === 'efectivo' || isCombinedPayment) && (
                <div className="space-y-2 border border-violet-200 rounded-lg p-3 bg-violet-50/40">
                  {isCombinedPayment && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500 w-24 shrink-0 capitalize">{secondPaymentMethod}</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        value={secondInputAmount}
                        onChange={(e) => setSecondInputAmount(e.target.value)}
                        className="flex-1 border border-slate-300 rounded-md px-2 py-1.5 text-sm font-mono"
                      />
                    </div>
                  )}
                  {isCombinedPayment && secondPaid > 0 && (
                    <div className="flex items-center justify-between text-xs px-1">
                      <span className="text-slate-500">→ Efectivo a cobrar:</span>
                      <span className={`font-mono font-medium ${Math.max(0, total - secondPaid) === 0 ? 'text-emerald-600' : 'text-violet-700'}`}>
                        ${fmt(Math.max(0, total - secondPaid))}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500 w-24 shrink-0">Efectivo recibido</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={cashInputAmount}
                      onChange={(e) => setCashInputAmount(e.target.value)}
                      className="flex-1 border border-slate-300 rounded-md px-2 py-1.5 text-sm font-mono"
                    />
                  </div>
                  {vuelto > 0 && (
                    <div className="flex items-center justify-between text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-lg">
                      <span>Vuelto</span>
                      <span className="font-mono">${fmt(vuelto)}</span>
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-sm">ITBIS</Label>
                <Button
                  type="button"
                  variant={includeItbis ? "default" : "outline"}
                  onClick={toggleItbis}
                  disabled={paymentMethod === 'tarjeta' || (isCombinedPayment && secondPaymentMethod === 'tarjeta')}
                  className={includeItbis ? "w-full h-9 bg-emerald-600 hover:bg-emerald-700 text-white" : "w-full h-9"}
                >
                  {includeItbis ? "ITBIS activado" : "ITBIS desactivado"}
                </Button>
              </div>

              <div className="space-y-2 pt-3 border-t border-slate-200">
                <div className="flex justify-between text-sm">
                  <span>Subtotal:</span>
                  <span className="mono-font font-medium">${fmt(subtotal + cardSurchargeAmt)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>ITBIS {includeItbis ? '(18%)' : '(no aplicado)'}:</span>
                  <span className="mono-font font-medium">${fmt(itbis)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-slate-200">
                  <span>TOTAL:</span>
                  <span className="mono-font text-cyan-600">${fmt(total)}</span>
                </div>
              </div>

              <Button
                onClick={() => cajaOpen ? handleCheckout() : toast.warning('Abre un turno de caja para facturar')}
                disabled={loading || cart.length === 0}
                className={`w-full text-white h-10 text-base font-medium shadow-md ${cajaOpen ? 'bg-cyan-500 hover:bg-cyan-600 shadow-cyan-500/20' : 'bg-slate-400 cursor-not-allowed'}`}
                data-testid="mobile-checkout-button"
              >
                {loading ? 'Procesando...' : (
                  <>
                    <Receipt className="w-4 h-4 mr-2" />
                    {tailoringOrder ? 'Facturar / Registrar abono' : serviceOrder ? 'Cobrar orden de servicio' : 'Generar Factura'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Receipt Modal */}
      {receiptModal && !askUpdateEmail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
            <div className="bg-cyan-500 px-6 py-4 text-white">
              <h2 className="font-bold text-lg">Comprobante listo</h2>
              <p className="text-cyan-100 text-sm">{receiptModal.customerName || 'Cliente'}</p>
              {receiptModal.invoice?.change_amount > 0 && (
                <div className="mt-3 flex items-center justify-between bg-emerald-400/30 border border-emerald-300/50 rounded-xl px-4 py-2">
                  <span className="font-semibold text-white">💵 Vuelto</span>
                  <span className="font-mono font-bold text-xl text-white">
                    ${Number(receiptModal.invoice.change_amount).toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              )}
            </div>
            <div className="p-6 space-y-4">
              {receiptModal.customerEmail && receiptTimerActive && !showEmailInput && (
                <div className="space-y-2">
                  <p className="text-sm text-slate-600 text-center">
                    Enviando a <strong>{receiptModal.customerEmail}</strong> en {receiptTimer}s...
                  </p>
                  <div className="w-full bg-slate-100 rounded-full h-2">
                    <div className="bg-cyan-500 h-2 rounded-full transition-all duration-1000"
                      style={{ width: `${(receiptTimer / (user?.receipt_timer || 15)) * 100}%` }} />
                  </div>
                </div>
              )}
              {showEmailInput && (
                <div className="space-y-2">
                  <Label className="text-sm">Correo del cliente</Label>
                  <Input type="email" placeholder="cliente@ejemplo.com" value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleReceiptModalEmail()} autoFocus />
                </div>
              )}
              <div className="grid grid-cols-1 gap-2">
                {receiptModal.customerEmail && !showEmailInput && (
                  <button onClick={() => { setReceiptTimerActive(false); doSendEmail(receiptModal.customerEmail, receiptModal.tailoringOrderId, receiptModal.serviceOrderId, receiptModal.invoice?.id); }}
                    disabled={sendingReceipt}
                    className="flex items-center justify-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white font-medium py-3 px-4 rounded-xl transition-colors">
                    📧 {sendingReceipt ? 'Enviando...' : `Enviar a ${receiptModal.customerEmail}`}
                  </button>
                )}
                {!showEmailInput && (
                  <button onClick={() => { setReceiptTimerActive(false); setShowEmailInput(true); }}
                    className="flex items-center justify-center gap-2 border border-slate-200 hover:bg-slate-50 text-slate-700 font-medium py-3 px-4 rounded-xl transition-colors">
                    📧 {receiptModal.customerEmail ? 'Enviar a otro correo' : 'Enviar por correo'}
                  </button>
                )}
                {showEmailInput && (
                  <button onClick={handleReceiptModalEmail} disabled={sendingReceipt}
                    className="flex items-center justify-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white font-medium py-3 px-4 rounded-xl transition-colors">
                    📧 {sendingReceipt ? 'Enviando...' : 'Enviar'}
                  </button>
                )}
                <button onClick={() => { setReceiptTimerActive(false); downloadAndPrint(receiptModal.tailoringOrderId, receiptModal.serviceOrderId, receiptModal.invoice?.id); closeReceiptModal(); }}
                  className="flex items-center justify-center gap-2 border border-slate-200 hover:bg-slate-50 text-slate-700 font-medium py-3 px-4 rounded-xl transition-colors">
                  🖨️ Imprimir comprobante
                </button>
                <button onClick={closeReceiptModal}
                  className="flex items-center justify-center gap-2 text-slate-400 hover:text-slate-600 text-sm font-medium py-2 px-4 rounded-xl transition-colors">
                  Omitir
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ask update email */}
      {askUpdateEmail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6 space-y-4">
            <h2 className="font-bold text-lg">¿Actualizar correo del cliente?</h2>
            <p className="text-sm text-slate-600">
              ¿Deseas guardar <strong>{askUpdateEmail}</strong> como correo de <strong>{receiptModal?.customerName}</strong>?
            </p>
            <div className="flex gap-2">
              <button onClick={() => confirmUpdateEmail(false)}
                className="flex-1 border border-slate-200 hover:bg-slate-50 text-slate-700 font-medium py-2 px-4 rounded-xl">
                No, solo enviar
              </button>
              <button onClick={() => confirmUpdateEmail(true)}
                className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-white font-medium py-2 px-4 rounded-xl">
                Sí, actualizar
              </button>
            </div>
          </div>
        </div>
      )}
      </div>

      {/* Delivery prompt — shown after billing a fully-paid tailoring or service order */}
      {deliveryPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6 space-y-4">
            <div className="text-center space-y-1">
              <div className="text-3xl">📦</div>
              <h2 className="font-bold text-lg text-slate-900">
                {deliveryPrompt.type === 'service' ? '¿Orden entregada?' : '¿Pedido entregado?'}
              </h2>
              <p className="text-sm text-slate-600">
                {deliveryPrompt.type === 'service' ? 'La orden' : 'El pedido'} <strong>{deliveryPrompt.orderCode}</strong> ha sido pagado en su totalidad.
                ¿El cliente recibió {deliveryPrompt.type === 'service' ? 'el servicio' : 'las prendas'} ahora?
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  const { orderId, type } = deliveryPrompt;
                  const listPath = type === 'service' ? '/service-orders' : '/tailoring-orders';
                  const updateFn = type === 'service' ? updateServiceOrderStatus : updateTailoringOrderStatus;
                  setDeliveryPrompt(null);
                  updateFn(orderId, 'ENTREGADO')
                    .then(() => toast.success('Marcado como Entregado'))
                    .catch(() => toast.error('No se pudo actualizar el estado'))
                    .finally(() => {
                      pendingNavigateRef.current = null;
                      navigate(listPath);
                    });
                }}
                className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-white font-medium py-2.5 px-4 rounded-xl transition-colors"
              >
                Sí, marcar Entregado
              </button>
              <button
                onClick={() => {
                  const listPath = deliveryPrompt.type === 'service' ? '/service-orders' : '/tailoring-orders';
                  setDeliveryPrompt(null);
                  pendingNavigateRef.current = null;
                  navigate(listPath);
                }}
                className="flex-1 border border-slate-200 hover:bg-slate-50 text-slate-700 font-medium py-2.5 px-4 rounded-xl transition-colors"
              >
                No, después
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image lightbox */}
      {enlargedImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setEnlargedImage(null)}
        >
          <div className="relative max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={enlargedImage.url}
              alt={enlargedImage.name}
              className="w-full max-h-[80vh] object-contain rounded-xl shadow-2xl"
            />
            <p className="text-center text-white text-sm mt-3 font-medium">{enlargedImage.name}</p>
            <button
              onClick={() => setEnlargedImage(null)}
              className="absolute -top-3 -right-3 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg text-slate-600 hover:text-slate-900"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default POS;